"use client";

import { useDetectGPU } from "@react-three/drei";
import { useState, createContext, useContext, ReactNode, useRef, MutableRefObject } from "react";
import { useIsClient } from "usehooks-ts";
import { useReducedMotion } from "@/hooks/useFramerMotion";
import type { Object3D } from "three";
import { HologramProvider } from "./HologramContext";
type Vector3 = {
  x: number;
  y: number;
  z: number;
};
export type QualityProfile = {
  tier: "low" | "medium" | "high";
  dpr: [number, number];
  shadows: boolean;
  bloom: boolean;
  starCount: number;
  galaxyMaxComplexity: number;
  planetComplexity: number;
  planetClouds: boolean;
  satellitesCastShadow: boolean;
  backdrop: {
    maxFps: number;
    maxHeight: number;
  };
  banner: {
    maxFps: number;
    maxHeight: number;
    satellites: number;
    dressing: boolean;
    ships: boolean;
  };
};
const QUALITY_PROFILES: Record<QualityProfile["tier"], QualityProfile> = {
  low: {
    tier: "low",
    dpr: [0.75, 1],
    shadows: false,
    // Galaxy glow is part of the look, not optional detail. Its two tiny
    // scene renders update at half the backdrop's tier-limited cadence.
    bloom: true,
    starCount: 1666,
    galaxyMaxComplexity: 0.15,
    planetComplexity: 5,
    planetClouds: false,
    satellitesCastShadow: false,
    backdrop: {
      maxFps: 20,
      maxHeight: 450
    },
    banner: {
      maxFps: 30,
      maxHeight: 600,
      satellites: 1,
      dressing: false,
      ships: false
    }
  },
  medium: {
    tier: "medium",
    dpr: [0.85, 1],
    shadows: false,
    bloom: true,
    starCount: 3333,
    galaxyMaxComplexity: 0.5,
    planetComplexity: 8,
    planetClouds: true,
    satellitesCastShadow: false,
    backdrop: {
      maxFps: 30,
      maxHeight: 600
    },
    banner: {
      maxFps: 60,
      maxHeight: 900,
      satellites: 3,
      dressing: true,
      ships: true
    }
  },
  high: {
    tier: "high",
    dpr: [1, 2],
    shadows: true,
    bloom: true,
    starCount: 15000,
    galaxyMaxComplexity: 1,
    planetComplexity: 13,
    planetClouds: true,
    satellitesCastShadow: true,
    backdrop: {
      maxFps: 60,
      maxHeight: 720
    },
    banner: {
      maxFps: 60,
      maxHeight: 1350,
      satellites: 10,
      dressing: true,
      ships: true
    }
  }
};
export interface SceneContextProps {
  webGLAvailable: boolean;
  disableWebGL: () => void;
  quality: "low" | "medium" | "high";
  profile: QualityProfile;
  is404: boolean;
  setIs404: (_state: boolean) => void;
  clientBannerScene: {
    sunPosition: Vector3 | undefined;
    sunAngle: number;
    // Written by MassifIcon per frame; Planet feeds them to the unlit planet
    // shader's accent light (scene lights don't reach csm_FragColor output).
    iconLightPos?: Vector3;
    iconLightColor?: {
      r: number;
      g: number;
      b: number;
    };
    // Tap-to-focus (banner): mesh the camera Rig should frame, or null.
    // Written by Planet onClick / the click-catcher; read by Rig.
    focusTarget: Object3D | null;
    // Damped orbit-speed factor (1 free-running → ~0.1 while focused).
    // Single writer: Satellites' useFrame; moons read it too.
    focusSlow: number;
  };
  isFooterContactHovered: MutableRefObject<boolean>;
  reduceMotion: boolean;
}
const context = createContext<SceneContextProps>(null!);
export const SceneContextProvider = ({
  children
}: {
  children: ReactNode;
}) => {
  const isClient = useIsClient();
  const isFooterContactHovered = useRef(false);
  const [is404, setIs404] = useState(false);
  const [webGLAvailable, setWebGLAvailable] = useState(true);
  const GPU = useDetectGPU({
    benchmarksURL: "/benchmarks-min"
  });

  // Tier 2 GPUs should receive the middle budget, not the high desktop preset.
  let quality: SceneContextProps["quality"] = GPU.tier >= 3 ? "high" : "medium";
  if (GPU.isMobile) {
    quality = "medium";
  }
  if (GPU.tier < 2) {
    quality = "low";
  }
  if (isClient) {
    const {
      userAgent,
      platform,
      maxTouchPoints
    } = window.navigator;
    // Mac Safari masks the GPU (renderer = "Apple GPU"), so detect-gpu can't
    // benchmark it and reports a low tier on hardware that's anything but.
    // Rescue it to medium. Version-agnostic on purpose: this was once pinned
    // to `Version/18` and silently rotted back to low when Safari 26 shipped.
    // `Version/<n> … Safari` only matches real Safari (Chrome has no
    // `Version/`); maxTouchPoints excludes iPads masquerading as MacIntel.
    const isMacSafari = /Version\/\d[\d.]*.+Safari/.test(userAgent) && platform === "MacIntel" && maxTouchPoints === 0;
    if (isMacSafari && GPU.gpu?.toLowerCase().includes("apple") && !GPU.isMobile) {
      quality = "medium";
    }
    const forcedTier = new URLSearchParams(window.location.search).get("tier");
    if (forcedTier === "low" || forcedTier === "medium" || forcedTier === "high") {
      quality = forcedTier;
    }
  }
  const profile = QUALITY_PROFILES[quality];

  // Respect prefers-reduced-motion across the 3D scenes. `?motion=reduce|full`
  // is a dev/testing override (mirrors `?tier=`), since headless 3D verification
  // is unreliable — it lets a focused tab toggle the behaviour without changing
  // OS settings.
  const prefersReduced = useReducedMotion() ?? false;
  let reduceMotion = prefersReduced;
  if (isClient) {
    const motion = new URLSearchParams(window.location.search).get("motion");
    if (motion === "reduce") reduceMotion = true;
    if (motion === "full") reduceMotion = false;
  }

  // Stable identity: per-frame mutable scratch shared across scenes (sun /
  // icon light, tap-to-focus). A per-render literal would wipe one-shot writes
  // like focusTarget whenever this provider re-renders.
  const clientBannerSceneRef = useRef<SceneContextProps["clientBannerScene"]>({
    sunPosition: undefined,
    sunAngle: 0,
    focusTarget: null,
    focusSlow: 1
  });
  const sceneContextValues = {
    webGLAvailable,
    disableWebGL: () => setWebGLAvailable(false),
    quality,
    profile,
    is404,
    setIs404,
    clientBannerScene: clientBannerSceneRef.current,
    isFooterContactHovered,
    reduceMotion
  };
  return <context.Provider value={sceneContextValues}>
      {/* Keep loading boundaries local to scenes: wrapping the route tree here
          intercepts notFound() during SSR and triggers hydration recovery. */}
      <HologramProvider>{children}</HologramProvider>
    </context.Provider>;
};
export const useSceneContext = () => {
  const sceneContext = useContext(context);
  if (!sceneContext) {
    throw new Error("useSceneContext must be used within a SceneContext.Provider");
  }
  return sceneContext;
};